import { REPORT_TYPES } from '../consts/reportTypes';
import { hasExtractionStarted, potentialExtractionCount } from './periodProgress';

export type StageGateId =
  'jurisdictions' | 'files' | 'mapping' | 'extraction' | 'transformation' | 'critical';

export interface StageGate {
  id: StageGateId;
  message: string;
  blocksFrom: string;
  stageKey: string;
}

export interface StageGateOptions {
  period?: any;
  reportType?: string;
  hasMapping?: boolean;
  enabled?: boolean;
}

export const STAGE_GATE_MESSAGES: Record<StageGateId, string> = {
  jurisdictions: 'Please add at least one jurisdiction first.',
  files: 'Please upload the source file first.',
  mapping: 'Please complete the Mapping step first.',
  extraction: 'Please add at least one extraction first.',
  transformation: "Please copy the previous period's transformation actions first.",
  critical: 'You have a critical issue. Please fix it before proceeding to Report Generation.'
};

const SEQUENCE = [
  'aif',
  'jurisdictions',
  'files',
  'mapping',
  'extraction',
  'lookup',
  'transformation',
  'output_data',
  'report',
  'approval',
  'submission'
];

const ALIASES: Record<string, string> = {
  aifs: 'aif',
  source_files: 'files',
  setup: 'mapping',
  lookup_tables: 'lookup'
};

const positionOf = (key: string): number => SEQUENCE.indexOf(ALIASES[key] ?? key);

const hasSourceFiles = (period: any): boolean =>
  typeof period.periodFileCount === 'number' ? period.periodFileCount > 0 : period.file_count > 0;

// A run that produced results had actions to run, whatever the automation rows say.
const hasTransformationRun = (period: any): boolean =>
  period.transformation_progress_status === 'Completed' ||
  period.transformation_stats?.successful_transformations_number > 0;

interface GateRule extends StageGate {
  met: (period: any) => boolean;
  applies: (options: StageGateOptions) => boolean;
}

const RULES: GateRule[] = [
  {
    id: 'jurisdictions',
    message: STAGE_GATE_MESSAGES.jurisdictions,
    blocksFrom: 'files',
    stageKey: 'jurisdictions',
    met: period => period.jurisdiction_count > 0,
    applies: ({ reportType }) => reportType !== REPORT_TYPES.CUSTOM
  },
  {
    id: 'files',
    message: STAGE_GATE_MESSAGES.files,
    blocksFrom: 'mapping',
    stageKey: 'source_files',
    met: hasSourceFiles,
    applies: () => true
  },
  {
    id: 'mapping',
    message: STAGE_GATE_MESSAGES.mapping,
    blocksFrom: 'extraction',
    stageKey: 'setup',
    met: period => potentialExtractionCount(period) > 0 || hasExtractionStarted(period),
    applies: ({ hasMapping }) => !!hasMapping
  },
  {
    id: 'extraction',
    message: STAGE_GATE_MESSAGES.extraction,
    blocksFrom: 'lookup',
    stageKey: 'extraction',
    met: period => hasExtractionStarted(period),
    applies: () => true
  },
  {
    id: 'transformation',
    message: STAGE_GATE_MESSAGES.transformation,
    blocksFrom: 'output_data',
    stageKey: 'transformation',
    met: period => period.transformationCount !== 0 || hasTransformationRun(period),
    applies: ({ period }) => !!period.template_type && period.template_type !== 'sratch'
  },
  {
    id: 'critical',
    message: STAGE_GATE_MESSAGES.critical,
    blocksFrom: 'report',
    stageKey: 'output_data',
    met: period => period.criticalIssueCount === 0,
    applies: ({ period }) => typeof period.criticalIssueCount === 'number'
  }
];

const isMinimalFiling = (period: any): boolean =>
  period.filing_type === 'PF_TRANS' || period.filing_type === 'PF_FINAL';

const isSettled = (period: any): boolean =>
  period.report_progress_status === 'Completed' ||
  period.approve_progress_status === 'Completed' ||
  period.submit_progress_status === 'Completed';

const asGate = (rule: GateRule): StageGate => ({
  id: rule.id,
  message: rule.message,
  blocksFrom: rule.blocksFrom,
  stageKey: rule.stageKey
});

const unmetRules = (options: StageGateOptions): GateRule[] => {
  const { period, reportType, enabled = true } = options;
  if (!enabled || !period) return [];
  if (isMinimalFiling(period) || isSettled(period)) return [];
  if (reportType === REPORT_TYPES.CONVERSION) return [];
  // Every included fund's national code is Nil Return — nothing to upload/extract/transform/model,
  // so none of the data-stage gates below ever apply (mirrors the PF_TRANS/PF_FINAL skip above).
  if (reportType === REPORT_TYPES.ANNEX_IV && period?.all_national_codes_nil_return === true) {
    return [];
  }
  return RULES.filter(rule => rule.applies(options) && !rule.met(period));
};

export const firstUnmetGate = (options: StageGateOptions): StageGate | null => {
  const [rule] = unmetRules(options);
  return rule ? asGate(rule) : null;
};

export const stageGateFor = (stageKey: string, options: StageGateOptions): StageGate | null => {
  const at = positionOf(stageKey);
  if (at < 0) return null;
  const rule = unmetRules(options).find(item => at >= positionOf(item.blocksFrom));
  return rule ? asGate(rule) : null;
};
